import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { PLANS } from "@/lib/plans";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CS } from "@/constants/testIds/clickshield";

export const Pricing = () => {
  const [annual, setAnnual] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  const startCheckout = async (plan) => {
    if (!user) {
      nav("/signup");
      return;
    }
    setLoadingId(plan.id);
    try {
      const lookup = annual ? plan.lookup.yearly : plan.lookup.monthly;
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  return (
    <section id="pricing" className="relative py-24 lg:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="font-mono text-xs tracking-widest uppercase text-cyan-neon">[06] Pricing</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mt-4 leading-tight">
            Plans that pay for themselves.
          </h2>
          <p className="mt-4 text-slate-400 font-light">Every plan includes a 14-day free trial. No credit card to start.</p>

          <div className="mt-8 inline-flex items-center gap-3 p-1.5 rounded-full border border-white/10 bg-[#0B101A]">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${!annual ? "bg-cyan-neon text-[#040914]" : "text-slate-300"}`}
            >
              Monthly
            </button>
            <button
              data-testid={CS.pricingToggle}
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${annual ? "bg-cyan-neon text-[#040914]" : "text-slate-300"}`}
            >
              Annual
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${annual ? "bg-[#040914]/20 text-[#040914]" : "bg-[#39FF14]/15 text-[#39FF14]"}`}>-20%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((plan, i) => {
            const price = annual ? plan.yearly : plan.monthly;
            return (
              <motion.div
                key={plan.id}
                data-testid={CS.planCard(plan.id)}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`relative rounded-2xl p-7 flex flex-col ${
                  plan.highlight
                    ? "neon-border bg-[#0B101A] glow-cyan"
                    : "bg-[#0B101A] border border-white/8"
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-cyan-neon text-[#040914] text-[10px] font-mono tracking-widest uppercase">
                    Most popular
                  </span>
                )}
                <h3 className="font-display text-xl text-white">{plan.name}</h3>
                <p className="text-xs text-slate-500 mt-1 h-8">{plan.tagline}</p>

                <div className="mt-5 flex items-end gap-1">
                  <span className="font-display text-4xl text-white tabular-nums">${price}</span>
                  <span className="text-slate-500 text-sm mb-1.5">/mo</span>
                </div>
                {annual && <p className="font-mono text-[10px] text-[#39FF14] mt-1">billed annually</p>}

                <div className="mt-5 pt-5 border-t border-white/8">
                  <p className="text-sm text-cyan-neon font-medium">{plan.adSpend}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{plan.clicks}</p>
                </div>

                <ul className="mt-5 space-y-2.5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlight ? "text-[#39FF14]" : "text-cyan-neon"}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={CS.planCta(plan.id)}
                  onClick={() => startCheckout(plan)}
                  disabled={loadingId === plan.id}
                  className={`mt-7 w-full py-3 rounded-full font-semibold text-sm transition-all inline-flex items-center justify-center gap-2 ${
                    plan.highlight
                      ? "bg-cyan-neon text-[#040914] hover:-translate-y-0.5 hover:glow-cyan"
                      : "border border-white/15 text-white hover:border-cyan-neon/50 hover:bg-white/5"
                  } disabled:opacity-60`}
                >
                  {loadingId === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start free trial"}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
